import { useTranslation } from 'react-i18next';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Row, Select, type SelectProps, theme, Typography } from 'antd';
import { useJobTechnicians } from 'pos-client/contexts/JobTechniciansContext.tsx';
import { type LinkedTechniciansGroup } from 'pos-client/hooks/useLocationLinkedTechnicians.ts';
import { sortStrings } from 'pos-client/utils/dataTableHelpers.tsx';
import type { BulkTechnicianModel } from 'pos-shared/models/technicianSchema';

import './TechnicianSelect.css';

type OmitKeys = 'loading' | 'fieldNames' | 'optionFilterProp' | 'options';

interface Props extends Omit<SelectProps, OmitKeys> {
  technicians: LinkedTechniciansGroup;
  selectedTechniciansBulk?: BulkTechnicianModel[];
  loading: boolean;
}

export function TechnicianSelect({
  technicians,
  selectedTechniciansBulk,
  loading,
  ...props
}: Props) {
  const { token } = theme.useToken();
  const { t } = useTranslation();
  const value = props.value;

  const { technicians: selectedTechnicians } = useJobTechnicians();

  return (
    <Select
      {...props}
      showSearch
      placeholder={t('technicians.selectTechnician')}
      style={{ width: 400, ...props.style }}
      className="technician-select"
      options={[
        ...(technicians.scheduledTechnicians.length > 0
          ? [
              {
                name: (
                  <Typography.Text strong>
                    {t('technicians.scheduledTechnicians')}
                  </Typography.Text>
                ),
                title: 'Scheduled Technicians',
                options: technicians.scheduledTechnicians
                  .map(({ name, id }: { name: string; id: string }) => ({
                    name: <Typography.Text strong>{name}</Typography.Text>,
                    id,
                    value: name,
                    key: id,
                    disabled: selectedTechniciansBulk
                      ? selectedTechniciansBulk?.some(
                          (selectedTech) => selectedTech.userId === id
                        )
                      : selectedTechnicians.some(
                          (selectedTech) => selectedTech.userId === id
                        ),
                  }))
                  .sort((a, b) =>
                    sortStrings({ value: a.value }, { value: b.value }, 'value')
                  ),
              },
            ]
          : []),
        ...(technicians.nonScheduledTechnicians.length > 0
          ? [
              {
                name: (
                  <Typography.Text strong>
                    {t('technicians.nonScheduledTechnicians')}
                  </Typography.Text>
                ),
                title: 'Non Scheduled Technicians',
                options: technicians.nonScheduledTechnicians
                  .map(({ name, id }: { name: string; id: string }) => ({
                    name: (
                      <Typography.Text
                        style={{
                          color:
                            value === id
                              ? token['gold-8']
                              : token.colorTextSecondary,
                        }}
                        strong
                      >
                        {name}
                      </Typography.Text>
                    ),
                    id,
                    value: name,
                    key: id,
                    disabled: selectedTechniciansBulk
                      ? selectedTechniciansBulk?.some(
                          (selectedTech) => selectedTech.userId === id
                        )
                      : selectedTechnicians.some(
                          (selectedTech) => selectedTech.userId === id
                        ),
                  }))
                  .sort((a, b) =>
                    sortStrings({ value: a.value }, { value: b.value }, 'value')
                  ),
              },
            ]
          : []),
      ]}
      optionFilterProp="value"
      styles={{
        popup: {
          root: { width: 400 },
        },
      }}
      popupRender={(menu) => (
        <>
          {menu}
          <Row
            style={{
              padding: token.marginLG,
              textAlign: 'center',
              whiteSpace: 'pre-line',
              color: token['gold-8'],
            }}
            align="top"
            justify="center"
          >
            <FontAwesomeIcon
              icon={faTriangleExclamation}
              color={token['gold-8']}
              style={{
                marginRight: token.marginXXS,
                marginTop: '4px',
              }}
            />
            {t('technicians.selectNonScheduledTechnician')}
          </Row>
        </>
      )}
      fieldNames={{
        label: 'name',
        value: 'id',
      }}
      loading={loading}
      disabled={loading}
    />
  );
}
